import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getAuth, Auth } from 'firebase/auth';
import { getFirestore, Firestore, enableMultiTabIndexedDbPersistence } from 'firebase/firestore';
import { getStorage, FirebaseStorage } from 'firebase/storage';

/**
 * Resolve the Firebase authDomain for the current host (P0 mobile sign-in fix).
 *
 * On the descout.xyz apex we serve the auth helper SAME-ORIGIN (via the /__/auth/* proxy in
 * next.config.js) so Safari/iOS third-party storage partitioning doesn't break sign-in.
 * Everywhere else we keep the cross-origin firebaseapp.com default (desktop popup works
 * there, and it keeps the human X-callback step scoped to ONE URL).
 *
 * SCOPE IS DELIBERATELY NARROW — descout.xyz apex ONLY:
 *   - NOT www / eurekanews.xyz (legacy alias keeps old behavior)
 *   - NOT *.vercel.app previews (they keep firebaseapp.com; desktop popup works there)
 *   - localhost dev is byte-identical to today (falls through to the env/default value)
 */
export function resolveAuthDomain(
  hostname: string | null,
  envAuthDomain: string | undefined
): string {
  if (hostname === 'descout.xyz') {
    return 'descout.xyz';
  }
  return envAuthDomain || 'eureka-meme.firebaseapp.com';
}

// Your web app's Firebase configuration
// This should match your existing 'eureka-meme' project
const firebaseConfig = {
  // Replace these with your actual Firebase config values
  // You can get these from: Firebase Console > Project Settings > General > Your apps
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY || 'dummy-key-for-build',
  authDomain: resolveAuthDomain(
    typeof window !== 'undefined' ? window.location.hostname : null,
    process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN
  ),
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || 'eureka-meme',
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || 'eureka-meme.appspot.com',
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '123456789',
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID || 'dummy-app-id',
};

// Initialize Firebase only if it hasn't been initialized yet
let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let persistenceInitialized = false;

try {
  app = !getApps().length ? initializeApp(firebaseConfig) : getApps()[0];
  
  // Initialize Firebase services
  auth = getAuth(app);
  db = getFirestore(app);
  storage = getStorage(app);

  // Enable offline persistence with multi-tab support
  // This allows multiple browser tabs to share the same IndexedDB cache
  // One tab becomes the "primary" and others sync through it via leader election
  // Guard against HMR re-execution in development
  if (typeof window !== 'undefined' && db && !persistenceInitialized) {
    persistenceInitialized = true;
    enableMultiTabIndexedDbPersistence(db).catch((err) => {
      if (err.code === 'failed-precondition') {
        // This can still happen in rare edge cases (e.g., different Firebase versions across tabs)
        console.warn('Firestore persistence: Could not enable multi-tab sync, using memory cache only');
      } else if (err.code === 'unimplemented') {
        // The current browser doesn't support persistence (e.g., private browsing mode)
        console.warn('Firestore persistence: Browser does not support offline persistence');
      } else {
        // Log but don't crash - Firestore will still work without persistence
        console.warn('Firestore persistence error (non-fatal):', err.code, err.message);
      }
    });
  }
} catch (error) {
  // During build time, we'll use null services
  app = null;
  auth = null;
  db = null;
  storage = null;
}

export { auth, db, storage };

export default app;

